import { useEffect, useMemo, useRef, useState } from "react";

import {
  createExercise,
  getExercises,
  updateExerciseLibrary,
  type Exercise,
} from "../lib/api";
import {
  draftOf,
  draftProblem,
  emptyDraft,
  MAX_SECONDARY,
  patchOf,
  toggleSecondary,
  traitsOf,
  withPrimary,
  type ExerciseDraft,
} from "../lib/exerciseDraft";
import {
  EQUIPMENT_ALL,
  MOVEMENTS,
  MUSCLE_GROUPS,
  equipmentLabel,
  movementLabel,
  muscleLabel,
} from "../lib/format";
import { guessTraits } from "../lib/guessTraits";
import { haptic, notify } from "../lib/telegram";
import { BusyLabel } from "./DotLoader";
import ExerciseIcon from "./ExerciseIcon";

interface Props {
  /** Правка своего упражнения. Нет — создание. */
  initial?: Exercise;
  /** Название из поиска, с которым пришли создавать. */
  name?: string;
  onSaved: (exercise: Exercise) => void;
  /** Взять существующее из банка вместо копии. Нет — подсказок «Может, это оно?» нет. */
  onPickExisting?: (exercise: Exercise) => void;
  onCancel: () => void;
}

type Step = "name" | "primary" | "equipment" | "movement" | "secondary" | "review";
type Picked = "primary" | "equipment" | "movement";

const FLOW: Step[] = ["name", "primary", "equipment", "movement", "secondary", "review"];
/** Шагов с вопросами — сводка шагом не считается. */
const QUESTIONS = FLOW.length - 1;

const TEXT: Record<Step, { title: string; hint: string }> = {
  name: { title: "Как называется?", hint: "Так упражнение будет видно в тренировке и поиске." },
  primary: { title: "Что работает в основном?", hint: "По этой мышце считается статистика." },
  equipment: { title: "С чем выполняется?", hint: "От снаряда — вес грифа в калькуляторе и иконка." },
  movement: { title: "Какое это движение?", hint: "По нему подбираются похожие для замены." },
  secondary: { title: "Что ещё подключается?", hint: `Необязательно, до ${MAX_SECONDARY}.` },
  review: { title: "Всё верно?", hint: "Нажмите на строку, чтобы поправить." },
};

const MOVEMENT_HINT: Record<string, string> = {
  push: "от себя: жимы, отжимания",
  pull: "к себе: тяги, подтягивания",
  squat: "присед, выпады, жим ногами",
  hinge: "наклон: становая, румынская, мост",
  isolation: "один сустав: сгибания, разведения",
  carry: "прогулка с весом",
  other: "кардио и всё остальное",
};

const MUSCLES = MUSCLE_GROUPS;
const SECONDARY = MUSCLE_GROUPS.filter((g) => g.key !== "other");

/**
 * Своё упражнение — пошаговым мастером (14.09.2026).
 *
 * Первая версия была одной формой из четырёх сеток чипов с пояснениями: на
 * телефоне она занимала три экрана, и заказчик назвал её громоздкой. Теперь один
 * вопрос на экран: выбрал — «Дальше». Автопереход после тапа заказчик отверг:
 * промахнулся плиткой — и уже на следующем шаге. По названию ответы подсказаны
 * заранее (guessTraits) — чаще всего остаётся только подтвердить. В конце
 * сводка; при правке мастер открывается сразу на ней, и тап по строке ведёт к
 * одному вопросу и обратно.
 */
export default function ExerciseForm({ initial, name = "", onSaved, onPickExisting, onCancel }: Props) {
  const editing = Boolean(initial);
  const [draft, setDraft] = useState<ExerciseDraft>(() =>
    initial ? draftOf(initial) : emptyDraft(name),
  );
  const [step, setStep] = useState<Step>(editing ? "review" : "name");
  // Правка из сводки: после ответа вернуться к ней, а не идти по порядку.
  const [returnTo, setReturnTo] = useState<Step | null>(null);
  // На что уже ответили или что подсказано: без этого «Дальше» пропускало бы
  // шаги, и упражнение уходило с «Другое» везде, как в первой версии.
  const [chosen, setChosen] = useState<Set<Picked>>(
    () => new Set(editing ? ["primary", "equipment", "movement"] : []),
  );
  const [twins, setTwins] = useState<Exercise[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  const q = draft.name_ru.trim();
  const guess = useMemo(() => guessTraits(q), [q]);

  useEffect(() => {
    if (editing || !onPickExisting || step !== "name" || q.length < 3) {
      setTwins([]);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      getExercises(q)
        .then((items) => live && setTwins(items.slice(0, 3)))
        .catch(() => undefined);
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q, editing, onPickExisting, step]);

  // Новый шаг — к его началу: иначе вопрос оказывается выше края шторки.
  useEffect(() => {
    root.current?.scrollIntoView({ block: "nearest" });
  }, [step]);

  const index = FLOW.indexOf(step);

  function go(next: Step) {
    haptic();
    setError(null);
    setStep(next);
  }

  function forward() {
    if (returnTo) {
      setReturnTo(null);
      go(returnTo);
      return;
    }
    if (step === "name" && !editing) {
      // Подсказываем только то, на что ещё не ответили.
      setDraft((d) => ({
        ...d,
        primary_muscle: chosen.has("primary") ? d.primary_muscle : guess.primary_muscle ?? d.primary_muscle,
        secondary_muscles: d.secondary_muscles.filter((m) => m !== guess.primary_muscle),
        equipment: chosen.has("equipment") ? d.equipment : guess.equipment ?? d.equipment,
        movement: chosen.has("movement") ? d.movement : guess.movement ?? d.movement,
      }));
      setChosen((c) => {
        const next = new Set(c);
        if (guess.primary_muscle) next.add("primary");
        if (guess.equipment) next.add("equipment");
        if (guess.movement) next.add("movement");
        return next;
      });
    }
    go(FLOW[index + 1]);
  }

  function back() {
    if (returnTo) {
      setReturnTo(null);
      go(returnTo);
    } else if (index > 0 && !(editing && step === "review")) {
      go(FLOW[index - 1]);
    } else {
      onCancel();
    }
  }

  function pick(field: Picked, value: string) {
    haptic("light");
    setError(null);
    setDraft((d) =>
      field === "primary" ? withPrimary(d, value) : { ...d, [field]: value },
    );
    setChosen((c) => new Set(c).add(field));
  }

  async function save() {
    const problem = draftProblem(draft);
    if (problem) {
      setError(problem);
      notify("warning");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let saved: Exercise;
      if (initial) {
        const patch = patchOf(initial, draft);
        saved = Object.keys(patch).length ? await updateExerciseLibrary(initial.id, patch) : initial;
      } else {
        saved = await createExercise(traitsOf(draft));
      }
      notify("success");
      onSaved(saved);
    } catch (e) {
      // 409 «уже есть такое» приходит текстом — его и показываем.
      setError(e instanceof Error ? e.message : String(e));
      notify("error");
    } finally {
      setBusy(false);
    }
  }

  const option = (
    key: string,
    label: string,
    on: boolean,
    onClick: () => void,
    extra?: { sub?: string; guessed?: boolean; disabled?: boolean },
  ) => (
    <button
      key={key}
      type="button"
      aria-pressed={on}
      disabled={extra?.disabled}
      className="wiz__opt"
      onClick={onClick}
    >
      {label}
      {extra?.sub && <span className="wiz__sub">{extra.sub}</span>}
      {extra?.guessed && !editing && <span className="wiz__badge">похоже</span>}
    </button>
  );

  const nextButton = (label = "Дальше", disabled = false) => (
    <button className="button button--wide" disabled={disabled} onClick={forward}>
      {returnTo ? "Готово" : label}
    </button>
  );

  const { title, hint } = TEXT[step];

  return (
    <div className="wiz" ref={root}>
      <div className="wiz__top">
        <button type="button" className="wiz__back" aria-label="Назад" onClick={back}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14.5 6l-6 6 6 6" />
          </svg>
        </button>
        {editing ? (
          <span className="wiz__count">своё упражнение</span>
        ) : (
          <>
            <div className="wiz__progress" aria-hidden="true">
              {Array.from({ length: QUESTIONS }, (_, i) => (
                <span key={i} className={`wiz__seg ${i <= Math.min(index, QUESTIONS - 1) ? "is-on" : ""}`} />
              ))}
            </div>
            <span className="wiz__count">
              {step === "review" ? "готово" : `${index + 1} / ${QUESTIONS}`}
            </span>
          </>
        )}
      </div>

      <div className="wiz__step" key={step}>
        <p className="wiz__title">{title}</p>
        <p className="wiz__hint">{hint}</p>

        {step === "name" && (
          <>
            <input
              className="input"
              value={draft.name_ru}
              maxLength={128}
              placeholder="Например, жим Z"
              enterKeyHint="next"
              autoFocus={!q}
              onChange={(e) => {
                setError(null);
                setDraft({ ...draft, name_ru: e.target.value });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && q.length >= 2) forward();
              }}
            />
            {twins.length > 0 && onPickExisting && (
              <div className="xform__twins">
                <p className="wiz__hint">Может, это оно? Возьмите готовое из банка.</p>
                <div className="picker">
                  {twins.map((exercise) => (
                    <button
                      key={exercise.id}
                      type="button"
                      className="picker__row"
                      onClick={() => {
                        haptic("medium");
                        onPickExisting(exercise);
                      }}
                    >
                      <ExerciseIcon exercise={exercise} />
                      <span className="picker__text">
                        <span className="picker__name">{exercise.name_ru}</span>
                        <span className="picker__meta">
                          {muscleLabel(exercise.primary_muscle)} · {equipmentLabel(exercise.equipment)}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {nextButton("Дальше", q.length < 2)}
          </>
        )}

        {step === "primary" && (
          <>
            <div className="wiz__grid">
              {MUSCLES.map((g) =>
                option(g.key, g.label, draft.primary_muscle === g.key, () => pick("primary", g.key), {
                  guessed: guess.primary_muscle === g.key,
                }),
              )}
            </div>
            {chosen.has("primary") && draft.primary_muscle && nextButton()}
          </>
        )}

        {step === "equipment" && (
          <>
            <div className="wiz__grid">
              {EQUIPMENT_ALL.map((g) =>
                option(g.key, g.label, chosen.has("equipment") && draft.equipment === g.key, () =>
                  pick("equipment", g.key), { guessed: guess.equipment === g.key },
                ),
              )}
            </div>
            {chosen.has("equipment") && nextButton()}
          </>
        )}

        {step === "movement" && (
          <>
            <div className="wiz__list">
              {MOVEMENTS.map((m) =>
                option(m.key, m.label, chosen.has("movement") && draft.movement === m.key, () =>
                  pick("movement", m.key), { sub: MOVEMENT_HINT[m.key], guessed: guess.movement === m.key },
                ),
              )}
            </div>
            {chosen.has("movement") && nextButton()}
          </>
        )}

        {step === "secondary" && (
          <>
            <div className="wiz__grid">
              {SECONDARY.filter((g) => g.key !== draft.primary_muscle).map((g) => {
                const on = draft.secondary_muscles.includes(g.key);
                return option(
                  g.key,
                  g.label,
                  on,
                  () => {
                    haptic("light");
                    setDraft(toggleSecondary(draft, g.key));
                  },
                  { disabled: !on && draft.secondary_muscles.length >= MAX_SECONDARY },
                );
              })}
            </div>
            {nextButton(draft.secondary_muscles.length ? "Дальше" : "Пропустить")}
          </>
        )}

        {step === "review" && (
          <>
            <div className="wiz__review">
              {(
                [
                  ["name", "Название", draft.name_ru.trim()],
                  ["primary", "Основная мышца", draft.primary_muscle ? muscleLabel(draft.primary_muscle) : "—"],
                  ["equipment", "Снаряд", equipmentLabel(draft.equipment)],
                  ["movement", "Движение", movementLabel(draft.movement)],
                  [
                    "secondary",
                    "Вспомогательные",
                    draft.secondary_muscles.map(muscleLabel).join(", ") || "нет",
                  ],
                ] as [Step, string, string][]
              ).map(([target, key, value]) => (
                <button
                  key={target}
                  type="button"
                  className="wiz__row"
                  onClick={() => {
                    setReturnTo("review");
                    go(target);
                  }}
                >
                  <span className="wiz__row-key">{key}</span>
                  <span className="wiz__row-val">{value}</span>
                </button>
              ))}
            </div>
            {error && <p className="lead lead--error">{error}</p>}
            <button className="button button--wide" disabled={busy} onClick={() => void save()}>
              {busy ? <BusyLabel text="Сохраняю" /> : editing ? "Сохранить" : "Добавить упражнение"}
            </button>
            <button type="button" className="link-button" disabled={busy} onClick={onCancel}>
              {editing ? "Отмена" : "Назад к поиску"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
